import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './UserManagement.css';

const PRIVILEGE_TEMPLATE = {
  dashboard: ['view'],
  assets: ['view', 'create', 'update', 'delete'],
  software: ['view', 'create', 'update', 'delete'],
  tickets: ['view', 'create', 'update', 'delete'],
  selfservice: ['view', 'create'],
  analytics: ['view'],
  documents: ['view', 'create', 'delete'],
  orders: ['view', 'create', 'accept'],
  cisbenchmarks: ['view', 'update'],
  utilization: ['view'],
  settings: ['view', 'update'],
};

function ensurePrivilegesObject(input) {
  const source = input && typeof input === 'object' && input.privileges && typeof input.privileges === 'object'
    ? input.privileges
    : {};
  const normalized = {};
  Object.entries(PRIVILEGE_TEMPLATE).forEach(([section, actions]) => {
    normalized[section] = {};
    actions.forEach((action) => {
      normalized[section][action] = Boolean(source?.[section]?.[action]);
    });
  });
  return { privileges: normalized };
}

function defaultViewOnlyPrivileges() {
  const normalized = {};
  Object.entries(PRIVILEGE_TEMPLATE).forEach(([section, actions]) => {
    normalized[section] = {};
    actions.forEach((action) => {
      normalized[section][action] = action === 'view';
    });
  });
  return { privileges: normalized };
}

const UserManagement = () => {
  const { authenticatedFetch, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUserId, setBusyUserId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [form, setForm] = useState({
    username: '',
    role: 'user',
    tenantAccessTenant: ''
  });
  const [createBusy, setCreateBusy] = useState(false);
  const [message, setMessage] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/users`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to load users');
        setUsers([]);
      } else {
        const rows = Array.isArray(data.users) ? data.users : [];
        setUsers(rows.map((row) => ({
          ...row,
          role: (row.role || 'user').toLowerCase(),
          privileges: ensurePrivilegesObject(row.privileges)
        })));
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Network error while loading users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchAvailableTenants = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenants`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(data.tenants)) {
        setAvailableTenants([]);
        return;
      }
      setAvailableTenants(
        data.tenants
          .map((t) => (typeof t?.name === 'string' ? t.name.trim() : ''))
          .filter(Boolean)
      );
    } catch (err) {
      console.error('Error loading tenants for access editor:', err);
      setAvailableTenants([]);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchUsers();
    fetchAvailableTenants();
  }, [fetchUsers, fetchAvailableTenants]);

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)) || null,
    [users, selectedUserId]
  );

  const clearMessageSoon = () => {
    setTimeout(() => setMessage(''), 2500);
  };

  const parseTenantAccess = (tenantAccessValue) => {
    const raw = typeof tenantAccessValue === 'string' ? tenantAccessValue.trim() : '';
    if (!raw) return { all: false, tenants: [] };
    if (raw.split(',').map((v) => v.trim()).includes('*')) {
      return { all: true, tenants: [] };
    }
    const seen = new Set();
    const tenants = raw
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      .filter((t) => {
        const key = t.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return { all: false, tenants };
  };

  const formatTenantAccess = (all, tenants) => {
    if (all) return '*';
    const seen = new Set();
    const deduped = (Array.isArray(tenants) ? tenants : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .filter((t) => {
        const key = t.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return deduped.join(',');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) return;
    if (form.role === 'user' && !form.tenantAccessTenant) {
      setError('Select a tenant for the new user.');
      return;
    }

    setCreateBusy(true);
    setError('');
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username.trim(),
          role: form.role,
          tenantAccess: form.role === 'admin' ? '*' : form.tenantAccessTenant,
          privileges: defaultViewOnlyPrivileges()
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Could not create user');
      } else {
        setMessage(`Created ${form.role} "${form.username.trim()}"`);
        clearMessageSoon();
        setForm({ username: '', role: 'user', tenantAccessTenant: '' });
        fetchUsers();
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Network error while creating user');
    } finally {
      setCreateBusy(false);
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (!targetUser || !window.confirm(`Delete user "${targetUser.username}"?`)) return;
    setBusyUserId(targetUser.id);
    setError('');
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/admin/users/${targetUser.id}`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Could not delete user');
      } else {
        setMessage(`Deleted "${targetUser.username}"`);
        clearMessageSoon();
        if (String(selectedUserId) === String(targetUser.id)) {
          setSelectedUserId(null);
        }
        fetchUsers();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Network error while deleting user');
    } finally {
      setBusyUserId(null);
    }
  };

  const togglePrivilege = (section, action, checked) => {
    setUsers((prev) => prev.map((u) => {
      if (String(u.id) !== String(selectedUserId)) return u;
      const nextPrivileges = ensurePrivilegesObject(u.privileges);
      nextPrivileges.privileges[section][action] = checked;
      return { ...u, privileges: nextPrivileges };
    }));
  };

  const handleRoleChange = (roleValue) => {
    setUsers((prev) => prev.map((u) => (
      String(u.id) === String(selectedUserId) ? { ...u, role: roleValue } : u
    )));
  };

  const handleSelectedUserTenantAccessAll = (checked) => {
    setUsers((prev) => prev.map((u) => (
      String(u.id) === String(selectedUserId)
        ? { ...u, tenantAccess: checked ? '*' : '' }
        : u
    )));
  };

  const toggleSelectedUserTenant = (tenantName, checked) => {
    setUsers((prev) => prev.map((u) => {
      if (String(u.id) !== String(selectedUserId)) return u;
      const parsed = parseTenantAccess(u.tenantAccess);
      if (parsed.all) {
        return { ...u, tenantAccess: checked ? '*' : '' };
      }
      const next = new Set(parsed.tenants);
      if (checked) next.add(tenantName);
      else next.delete(tenantName);
      return { ...u, tenantAccess: formatTenantAccess(false, Array.from(next)) };
    }));
  };

  const saveSelectedUserAccess = async () => {
    if (!selectedUser) return;
    setBusyUserId(selectedUser.id);
    setError('');
    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/admin/users/${selectedUser.id}/privileges`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role: selectedUser.role,
            tenantAccess: selectedUser.tenantAccess || '',
            privileges: ensurePrivilegesObject(selectedUser.privileges)
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.error || 'Could not save privileges');
      } else {
        setMessage(`Saved access for "${selectedUser.username}"`);
        clearMessageSoon();
      }
    } catch (err) {
      console.error('Error saving privileges:', err);
      setError('Network error while saving privileges');
    } finally {
      setBusyUserId(null);
    }
  };

  const selectedTenantAccess = selectedUser ? parseTenantAccess(selectedUser.tenantAccess) : { all: false, tenants: [] };

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <div>
          <h2>User Management</h2>
          <p>Portal user and access management.</p>
        </div>
        <button type="button" className="user-management-refresh" onClick={fetchUsers} disabled={loading}>
          <Icon name="refresh" size="small" />
          Refresh
        </button>
      </div>

      {(error || message) && (
        <div className={`user-management-feedback ${error ? 'error' : 'success'}`}>
          {error || message}
        </div>
      )}

      <form className="user-management-create" onSubmit={handleCreateUser}>
        <input
          type="text"
          placeholder="Username"
          value={form.username}
          onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
          required
        />
        <select
          value={form.role}
          onChange={(e) => {
            const nextRole = e.target.value;
            setForm((prev) => ({
              ...prev,
              role: nextRole,
              tenantAccessTenant: nextRole === 'admin' ? '' : prev.tenantAccessTenant
            }));
          }}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        {form.role === 'user' ? (
          <select
            value={form.tenantAccessTenant}
            onChange={(e) => setForm((prev) => ({ ...prev, tenantAccessTenant: e.target.value }))}
            required
          >
            <option value="">Select tenant access</option>
            {availableTenants.map((tenantName) => (
              <option key={`create-tenant-${tenantName}`} value={tenantName}>
                {tenantName}
              </option>
            ))}
          </select>
        ) : (
          <div className="user-management-inline-note">Admin defaults to all tenants (`*`).</div>
        )}
        <button type="submit" disabled={createBusy}>
          {createBusy ? 'Adding…' : 'Add user'}
        </button>
      </form>

      {loading ? (
        <div className="user-management-state">Loading users...</div>
      ) : (
        <div className="user-management-grid">
          <div className="user-management-table-wrap">
            <table className="user-management-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Tenant access</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((row) => (
                  <tr key={row.id} className={String(row.id) === String(selectedUserId) ? 'selected' : ''}>
                    <td>{row.username}</td>
                    <td>{row.role}</td>
                    <td>{row.status || 'active'}</td>
                    <td>{row.tenantAccess || '—'}</td>
                    <td className="user-management-row-actions">
                      <button type="button" onClick={() => setSelectedUserId(row.id)}>
                        Access
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleDeleteUser(row)}
                        disabled={busyUserId === row.id || user?.id === row.id}
                        title={user?.id === row.id ? 'Cannot delete your own account' : 'Delete user'}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="user-management-access-panel">
            {!selectedUser ? (
              <div className="user-management-state">Select a user to edit privileges.</div>
            ) : (
              <>
                <div className="access-panel-header">
                  <h3>{selectedUser.username}</h3>
                  <select
                    value={selectedUser.role}
                    onChange={(e) => handleRoleChange(e.target.value)}
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                </div>

                <div className="access-panel-scroller">
                  <div className="access-section">
                    <h4>Tenant Access</h4>
                    <div className="access-toggles">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedTenantAccess.all}
                          onChange={(e) => handleSelectedUserTenantAccessAll(e.target.checked)}
                        />
                        All tenants (`*`)
                      </label>
                    </div>
                    {!selectedTenantAccess.all && (
                      <div className="access-toggles">
                        {availableTenants.map((tenantName) => (
                          <label key={`access-tenant-${tenantName}`}>
                            <input
                              type="checkbox"
                              checked={selectedTenantAccess.tenants.includes(tenantName)}
                              onChange={(e) => toggleSelectedUserTenant(tenantName, e.target.checked)}
                            />
                            {tenantName}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {Object.entries(PRIVILEGE_TEMPLATE).map(([section, actions]) => (
                    <div key={section} className="access-section">
                      <h4>{section}</h4>
                      <div className="access-toggles">
                        {actions.map((action) => (
                          <label key={`${section}-${action}`}>
                            <input
                              type="checkbox"
                              checked={Boolean(selectedUser.privileges?.privileges?.[section]?.[action])}
                              onChange={(e) => togglePrivilege(section, action, e.target.checked)}
                            />
                            {action}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="save-access"
                  onClick={saveSelectedUserAccess}
                  disabled={busyUserId === selectedUser.id}
                >
                  {busyUserId === selectedUser.id ? 'Saving…' : 'Save access'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
